import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { MonthDropdown } from './MonthDropdown';
import { YearDropdown } from './YearDropdown';

interface CalendarProps {
  selectedDate: Date | null;
  onSelectDate: (date: Date) => void;
  fromDate?: Date;
  toDate?: Date;
  onClose: () => void;
  /**
   * Unique id for the calendar dialog (used by aria-controls)
   */
  id?: string;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

const FOCUSABLE_SELECTOR =
  'a[href], button, input, select, textarea, [tabindex], [contenteditable="true"], iframe, audio[controls], video[controls]';

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_OF_WEEK_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isDateOutOfRange(date: Date, fromDate?: Date, toDate?: Date): boolean {
  if (fromDate && date < fromDate) return true;
  if (toDate && date > toDate) return true;

  return false;
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function addMonths(date: Date, months: number): Date {
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();

  return new Date(target.getFullYear(), target.getMonth(), Math.min(date.getDate(), lastDay));
}

function startOfWeek(date: Date): Date {
  return addDays(date, -date.getDay());
}

function endOfWeek(date: Date): Date {
  return addDays(date, 6 - date.getDay());
}

// Clamp (constrain) the day number into the target month so prev/next, month, and year
// changes keep the same day. If that day does not exist - January 31 --> February - use
// the last day of the month instead of dropping focus or jumping to the 1st.
function clampToMonth(date: Date, monthStart: Date): Date {
  const lastDay = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0).getDate();

  return new Date(monthStart.getFullYear(), monthStart.getMonth(), Math.min(date.getDate(), lastDay));
}

function firstSelectableInMonth(monthStart: Date, fromDate?: Date, toDate?: Date): Date | null {
  const lastDay = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0).getDate();

  for (let day = 1; day <= lastDay; day++) {
    const candidate = new Date(monthStart.getFullYear(), monthStart.getMonth(), day);
    if (!isDateOutOfRange(candidate, fromDate, toDate)) {
      return candidate;
    }
  }

  return null;
}

function findSelectableDate(start: Date, stepDays: number, fromDate?: Date, toDate?: Date): Date | null {
  let candidate = start;

  for (let i = 0; i < 400; i++) {
    if (!isDateOutOfRange(candidate, fromDate, toDate)) {
      return candidate;
    }

    candidate = addDays(candidate, stepDays);

    if (fromDate && candidate < fromDate && stepDays < 0) {
      return null;
    }

    if (toDate && candidate > toDate && stepDays > 0) {
      return null;
    }
  }

  return null;
}

function getInitialMonth(selectedDate: Date | null, toDate?: Date): Date {
  if (selectedDate && !isNaN(selectedDate.getTime())) {
    return new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
  }

  if (toDate) {
    const currentDate = new Date();
    const currentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const toDateMonth = new Date(toDate.getFullYear(), toDate.getMonth(), 1);

    return toDateMonth > currentMonth ? currentMonth : toDateMonth;
  }

  const now = new Date();

  return new Date(now.getFullYear(), now.getMonth(), 1);
}

function getInitialFocusedDate(selectedDate: Date | null, currentMonth: Date, fromDate?: Date, toDate?: Date): Date {
  if (
    selectedDate &&
    !isNaN(selectedDate.getTime()) &&
    selectedDate.getFullYear() === currentMonth.getFullYear() &&
    selectedDate.getMonth() === currentMonth.getMonth() &&
    !isDateOutOfRange(selectedDate, fromDate, toDate)
  ) {
    return selectedDate;
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (
    today.getFullYear() === currentMonth.getFullYear() &&
    today.getMonth() === currentMonth.getMonth() &&
    !isDateOutOfRange(today, fromDate, toDate)
  ) {
    return today;
  }

  const firstSelectable = firstSelectableInMonth(currentMonth, fromDate, toDate);

  return firstSelectable ?? new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
}

export const Calendar: React.FC<CalendarProps> = ({
  selectedDate,
  onSelectDate,
  fromDate,
  toDate,
  onClose,
  id,
  'data-testid': dataTestId,
}) => {
  const baseTestId = dataTestId ?? 'calendar';
  const calendarId = id ?? baseTestId;
  const monthSelectId = `${calendarId}-month`;
  const yearSelectId = `${calendarId}-year`;
  const dialogRef = useRef<HTMLDivElement>(null);
  const focusedDayRef = useRef<HTMLButtonElement>(null);
  const moveGridFocusRef = useRef(true);

  const [currentMonth, setCurrentMonth] = useState(() => getInitialMonth(selectedDate, toDate));
  const [focusedDate, setFocusedDate] = useState(() =>
    getInitialFocusedDate(selectedDate, getInitialMonth(selectedDate, toDate), fromDate, toDate),
  );

  const isDateDisabled = useCallback(
    (date: Date): boolean => isDateOutOfRange(date, fromDate, toDate),
    [fromDate, toDate],
  );

  // Generate year options based on fromDate and toDate constraints
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    let minYear = currentYear - 80; // Default: 80 years back
    let maxYear = currentYear; // Default: current year

    // If toDate is provided, use it as the maximum year
    if (toDate) {
      maxYear = toDate.getFullYear();
    }

    // If fromDate is provided, use it as the minimum year
    if (fromDate) {
      minYear = fromDate.getFullYear();
    }

    const years: number[] = [];
    for (let i = maxYear; i >= minYear; i--) {
      years.push(i);
    }

    return years;
  }, [fromDate, toDate]);

  const daysInMonth = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysCount = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysCount; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  }, [currentMonth]);

  const weeks = useMemo(() => {
    const result: (Date | null)[][] = [];

    for (let i = 0; i < daysInMonth.length; i += 7) {
      const week = daysInMonth.slice(i, i + 7);
      while (week.length < 7) {
        week.push(null);
      }
      result.push(week);
    }

    return result;
  }, [daysInMonth]);

  const isDateSelected = (date: Date): boolean => {
    if (!selectedDate) return false;

    return isSameDay(date, selectedDate);
  };

  const syncFocusedDateToMonth = (monthStart: Date) => {
    const clamped = clampToMonth(focusedDate, monthStart);
    if (!isDateDisabled(clamped)) {
      setFocusedDate(clamped);

      return;
    }

    const firstSelectable = firstSelectableInMonth(monthStart, fromDate, toDate);
    if (firstSelectable) {
      setFocusedDate(firstSelectable);
    }
  };

  const handlePreviousMonth = () => {
    const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    setCurrentMonth(nextMonth);
    syncFocusedDateToMonth(nextMonth);
  };

  const handleNextMonth = () => {
    const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    setCurrentMonth(nextMonth);
    syncFocusedDateToMonth(nextMonth);
  };

  // Generate month options with disabled states
  const monthOptions = useMemo(() => {
    const year = currentMonth.getFullYear();

    return MONTHS.map((month, index) => {
      const firstDayOfMonth = new Date(year, index, 1);
      const lastDayOfMonth = new Date(year, index + 1, 0);

      let disabled = false;
      if (toDate && firstDayOfMonth > toDate) disabled = true;
      if (fromDate && lastDayOfMonth < fromDate) disabled = true;

      return {
        disabled,
        label: month,
        value: index,
      };
    });
  }, [currentMonth, fromDate, toDate]);

  const handleMonthChange = (month: number) => {
    const nextMonth = new Date(currentMonth.getFullYear(), month, 1);
    setCurrentMonth(nextMonth);
    syncFocusedDateToMonth(nextMonth);
  };

  const handleYearChange = (year: number) => {
    const nextMonth = new Date(year, currentMonth.getMonth(), 1);
    setCurrentMonth(nextMonth);
    syncFocusedDateToMonth(nextMonth);
  };

  const handleDateClick = (date: Date) => {
    if (!isDateDisabled(date)) {
      onSelectDate(date);
    }
  };

  const focusDate = (candidate: Date, skipStep: number) => {
    const next = isDateDisabled(candidate) ? findSelectableDate(candidate, skipStep, fromDate, toDate) : candidate;
    if (!next) return;

    if (next.getMonth() !== currentMonth.getMonth() || next.getFullYear() !== currentMonth.getFullYear()) {
      setCurrentMonth(new Date(next.getFullYear(), next.getMonth(), 1));
    }

    moveGridFocusRef.current = true;
    setFocusedDate(next);
  };

  const handleDayKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, date: Date) => {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        focusDate(addDays(date, -1), -1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        focusDate(addDays(date, 1), 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusDate(addDays(date, -7), -1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        focusDate(addDays(date, 7), 1);
        break;
      case 'Home':
        e.preventDefault();
        focusDate(startOfWeek(date), 1);
        break;
      case 'End':
        e.preventDefault();
        focusDate(endOfWeek(date), -1);
        break;
      case 'PageUp':
        e.preventDefault();
        focusDate(e.ctrlKey ? addMonths(date, -12) : addMonths(date, -1), -1);
        break;
      case 'PageDown':
        e.preventDefault();
        focusDate(e.ctrlKey ? addMonths(date, 12) : addMonths(date, 1), 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleDateClick(date);
        break;
      default:
        break;
    }
  };

  const getFocusableElements = useCallback(() => {
    if (!dialogRef.current) return [];

    return Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((element) => {
      const isDisabled = element.matches(':disabled') || element.getAttribute('aria-disabled') === 'true';
      const isHiddenInput = element.matches('input[type="hidden"]');
      const isHiddenByStyle =
        ['none', 'hidden'].includes(getComputedStyle(element).display) ||
        getComputedStyle(element).visibility === 'hidden';
      const isHidden =
        element.hidden || isHiddenByStyle || element.closest('[hidden], [inert], [aria-hidden="true"]') !== null;

      return !isDisabled && !isHiddenInput && element.tabIndex >= 0 && !isHidden;
    });
  }, []);

  const handleDialogKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();

      return;
    }

    if (e.key !== 'Tab') return;

    e.stopPropagation();

    const focusableElements = getFocusableElements();
    if (focusableElements.length === 0) {
      e.preventDefault();
      dialogRef.current?.focus();

      return;
    }

    const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);

    if (e.shiftKey && (currentIndex <= 0 || currentIndex === -1)) {
      e.preventDefault();
      focusableElements[focusableElements.length - 1].focus();
    } else if (!e.shiftKey && (currentIndex === -1 || currentIndex === focusableElements.length - 1)) {
      e.preventDefault();
      focusableElements[0].focus();
    }
  };

  useEffect(() => {
    if (moveGridFocusRef.current) {
      focusedDayRef.current?.focus();
      focusedDayRef.current?.scrollIntoView?.({ block: 'nearest' });
      moveGridFocusRef.current = false;
    }
  }, [focusedDate, currentMonth]);

  const monthYearLabel = `${MONTHS[currentMonth.getMonth()]} ${currentMonth.getFullYear()}`;

  return (
    <div
      ref={dialogRef}
      aria-label='Calendar'
      aria-modal='true'
      className='ds-c-popover ds-c-popover--calendar'
      data-testid={baseTestId}
      id={calendarId}
      role='dialog'
      tabIndex={-1}
      onKeyDown={handleDialogKeyDown}>
      <div
        className='ds-c-calendar'
        data-testid={`${baseTestId}-container`}>
        <div
          className='ds-c-calendar__navigation'
          data-testid={`${baseTestId}-nav`}>
          <button
            aria-label='Previous month'
            className='ds-c-calendar__previous-month'
            data-testid={`${baseTestId}-previous-month`}
            type='button'
            onClick={handlePreviousMonth}>
            <svg
              aria-hidden='true'
              fill='none'
              focusable='false'
              height='20'
              stroke='currentColor'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              viewBox='0 0 24 24'
              width='20'
              xmlns='http://www.w3.org/2000/svg'>
              <polyline points='15 18 9 12 15 6' />
            </svg>
          </button>
          <div
            className='ds-c-calendar__month-year'
            data-testid={`${baseTestId}-month-year`}>
            <MonthDropdown
              ariaLabel='Select month'
              data-testid={`${baseTestId}-month-dropdown`}
              id={monthSelectId}
              name='calendar-month'
              options={monthOptions}
              value={currentMonth.getMonth()}
              onChange={handleMonthChange}
            />
            <YearDropdown
              ariaLabel='Select year'
              data-testid={`${baseTestId}-year-dropdown`}
              id={yearSelectId}
              name='calendar-year'
              options={yearOptions}
              value={currentMonth.getFullYear()}
              onChange={handleYearChange}
            />
          </div>
          <button
            aria-label='Next month'
            className='ds-c-calendar__next-month'
            data-testid={`${baseTestId}-next-month`}
            type='button'
            onClick={handleNextMonth}>
            <svg
              aria-hidden='true'
              fill='none'
              focusable='false'
              height='20'
              stroke='currentColor'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              viewBox='0 0 24 24'
              width='20'
              xmlns='http://www.w3.org/2000/svg'>
              <polyline points='9 18 15 12 9 6' />
            </svg>
          </button>
        </div>
        <div
          aria-label={monthYearLabel}
          className='ds-c-calendar__grid'
          role='grid'>
          <div
            className='ds-c-calendar__days-of-week'
            data-testid={`${baseTestId}-days-of-week`}
            role='row'>
            {DAYS_OF_WEEK.map((day, index) => (
              <div
                key={day}
                aria-label={DAYS_OF_WEEK_FULL[index]}
                className='ds-c-calendar__day-of-week'
                data-testid={`${baseTestId}-day-of-week-${day.toLowerCase()}`}
                role='columnheader'>
                {day}
              </div>
            ))}
          </div>
          <div
            className='ds-c-calendar__days'
            data-testid={`${baseTestId}-days`}>
            {weeks.map((week, weekIndex) => (
              <div
                key={`week-${weekIndex}`}
                className='ds-c-calendar__week'
                role='row'>
                {week.map((date, dayIndex) => {
                  const cellIndex = weekIndex * 7 + dayIndex;

                  if (!date) {
                    return (
                      <div
                        key={`empty-${cellIndex}`}
                        className='ds-c-calendar__day ds-c-calendar__day--empty'
                        data-testid={`${baseTestId}-day-empty-${cellIndex}`}
                        role='gridcell'
                      />
                    );
                  }

                  const disabled = isDateDisabled(date);
                  const selected = isDateSelected(date);
                  const isFocused = isSameDay(date, focusedDate);

                  return (
                    <div
                      key={`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`}
                      aria-disabled={disabled || undefined}
                      aria-selected={selected}
                      role='gridcell'>
                      <button
                        ref={isFocused ? focusedDayRef : undefined}
                        aria-label={`${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`}
                        aria-selected={selected}
                        className={`ds-c-calendar__day ${selected ? 'ds-c-calendar__day--selected' : ''} ${
                          disabled ? 'ds-c-calendar__day--disabled' : ''
                        }`}
                        data-testid={`${baseTestId}-day-${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`}
                        disabled={disabled}
                        tabIndex={isFocused && !disabled ? 0 : -1}
                        type='button'
                        onClick={() => handleDateClick(date)}
                        onKeyDown={(e) => handleDayKeyDown(e, date)}>
                        {date.getDate()}
                      </button>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
